import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { BoxCoordinates } from '@/types';

interface BoxOverlayProps {
  imageRef: React.RefObject<HTMLImageElement>;
  box: BoxCoordinates | null;
  onBoxChange: (box: BoxCoordinates | null) => void;
  disabled?: boolean;
}

const MIN_BOX_SIZE_NORMALIZED = 0.02; // ~2% of image — ignore accidental taps

export const BoxOverlay: React.FC<BoxOverlayProps> = ({
  imageRef,
  box,
  onBoxChange,
  disabled = false,
}) => {
  const [imgRect, setImgRect] = useState<DOMRect | null>(null);
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragEnd, setDragEnd] = useState<{ x: number; y: number } | null>(null);
  const activeDragRef = useRef(false);

  useEffect(() => {
    const img = imageRef.current;
    if (!img) return;
    const update = () => setImgRect(img.getBoundingClientRect());
    update();
    const observer = new ResizeObserver(update);
    observer.observe(img);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    img.addEventListener('load', update);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      img.removeEventListener('load', update);
    };
  }, [imageRef]);

  const clientToNormalized = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      if (!imgRect) return { x: 0, y: 0 };
      return {
        x: Math.max(0, Math.min(1, (clientX - imgRect.left) / imgRect.width)),
        y: Math.max(0, Math.min(1, (clientY - imgRect.top) / imgRect.height)),
      };
    },
    [imgRect]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (disabled) return;
      e.preventDefault();
      const coord = clientToNormalized(e.clientX, e.clientY);
      setDragStart(coord);
      setDragEnd(coord);
      activeDragRef.current = true;

      const onMove = (ev: PointerEvent) => {
        if (!activeDragRef.current) return;
        setDragEnd(clientToNormalized(ev.clientX, ev.clientY));
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);

        const end = clientToNormalized(ev.clientX, ev.clientY);
        activeDragRef.current = false;
        setDragStart(null);
        setDragEnd(null);

        const w = Math.abs(end.x - coord.x);
        const h = Math.abs(end.y - coord.y);
        if (w > MIN_BOX_SIZE_NORMALIZED && h > MIN_BOX_SIZE_NORMALIZED) {
          onBoxChange({ x1: coord.x, y1: coord.y, x2: end.x, y2: end.y });
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [disabled, clientToNormalized, onBoxChange]
  );

  if (!imgRect) return null;

  const activeBox = dragStart && dragEnd
    ? { x1: dragStart.x, y1: dragStart.y, x2: dragEnd.x, y2: dragEnd.y }
    : box;

  return (
    <div
      className="fixed pointer-events-auto"
      style={{
        left: imgRect.left,
        top: imgRect.top,
        width: imgRect.width,
        height: imgRect.height,
        cursor: disabled ? 'default' : 'crosshair',
      }}
      onPointerDown={handlePointerDown}
    >
      {activeBox && (
        <div
          className="absolute border-2 border-dashed border-primary bg-primary/10 pointer-events-none"
          style={{
            left: `${Math.min(activeBox.x1, activeBox.x2) * 100}%`,
            top: `${Math.min(activeBox.y1, activeBox.y2) * 100}%`,
            width: `${Math.abs(activeBox.x2 - activeBox.x1) * 100}%`,
            height: `${Math.abs(activeBox.y2 - activeBox.y1) * 100}%`,
          }}
        >
          <span className="absolute -top-5 left-0 bg-primary text-white text-[9px] font-bold px-1.5 py-0.5 rounded-sm">
            MARKIERT
          </span>
        </div>
      )}
    </div>
  );
};
